import React, { useState, useContext } from "react";
import { Box, Typography } from "@mui/material";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import { Add } from "@mui/icons-material";
import { EditProtocolDialog } from "../protocol/EditProtocolDialog";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import axios from "axios";
import { useSnackbar } from "notistack";
import { CopyProtocolDialog } from "./CopyProtocolDialog";

export const ProtocolItem = ({ id, protocolname, onProtocolUpdate }) => {
  const { token } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);
  const [openEditDialog, setOpenEditDialog] = useState(false); // New state for confirmation dialog
  const [openCopyDialog, setOpenCopyDialog] = useState(false); // New state for confirmation dialog
  const [protocolToEdit, setProtocolToEdit] = useState(null); // New state for the user to be deleted
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/ebprotocol/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setProtocolToEdit(response.data);
    setOpenEditDialog(true);
  };

  const handleCopyClick = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/ebprotocol/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setProtocolToEdit(response.data);
    setOpenCopyDialog(true);
  };

  const handleCancelEdit = () => {
    setOpenEditDialog(false);
  };

  const handleCancelCopy = () => {
    setOpenCopyDialog(false);
  };

  const handleEditProtocol = async (data) => {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/ebprotocol/${data.id}`,
      {
        protocolname: data.protocolname,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    await axios.post(
      `${process.env.REACT_APP_API_URL}/ebprotexercise/bulkedit`,
      {
        protocolid: data.id,
        exercises: data.exercises,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    onProtocolUpdate(response.data); // Update the parent state
    enqueueSnackbar("Successfully Changed!", { variant: "success" });
  };

  const handleCopyProtocol = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/ebprotocol`,
        {
          protocolname: data.protocolname,
          conditions: data.conditions,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await axios.post(
        `${process.env.REACT_APP_API_URL}/ebprotexercise/bulkcreate`,
        {
          protocolid: response.data.id,
          exercises: data.exercises,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onProtocolUpdate(response.data); // Update the parent state
      enqueueSnackbar("Successfully Copied!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to copy protocol!", { variant: "error" });
    }
  };

  const handleDeleteProtocol = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/ebprotocol/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      await axios.post(
        `${process.env.REACT_APP_API_URL}/ebprotexercise/bulkdelete`,
        {
          protocolid: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onProtocolUpdate({ id, deleted: true }); // Notify parent about the deletion
      setOpenEditDialog(false);
      enqueueSnackbar("Successfully Deleted!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to delete protocol!", { variant: "error" });
    }
  };

  return (
    <>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{
          width: "475px",
          height: "120px",
          padding: 1,
          borderRadius: "5px",
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: darkMode
            ? getColors.thumbnailBorderDark
            : getColors.thumbnailBorderLight,
          background: darkMode
            ? getColors.thumbnnailBgDark
            : getColors.thumbnnailBgLight,
          cursor: "pointer",
          "&:hover": {
            borderColor: darkMode
              ? getColors.thumbnailTextDark
              : getColors.thumbnailTextLight,
          },
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 500,
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
              ml: "20px",
            }}
          >
            {protocolname}
          </Typography>
        </Box>
        <Box display="flex">
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            width="max-content"
            sx={{
              borderRadius: "50%",
              padding: "20px",
              color: "#009903",
              bgcolor: "#EAFFEF",
              alignItems: "center",
              "&:hover": {
                color: "green",
              },
              ml: 1,
              mr: 2,
            }}
            onClick={handleCopyClick} // Move the onClick handler here
          >
            <Add />
          </Box>
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            width="max-content"
            sx={{
              borderRadius: "50%",
              padding: "20px",
              color: "#007FFF",
              bgcolor: "#E9F3FF",
              alignItems: "center",
              "&:hover": {
                color: "blue",
              },
              mr: 2,
            }}
            onClick={handleClick} // Move the onClick handler here
          >
            <BorderColorOutlinedIcon />
          </Box>
        </Box>
      </Box>
      <EditProtocolDialog
        open={openEditDialog}
        onClose={handleCancelEdit}
        protocolData={protocolToEdit}
        onSave={handleEditProtocol}
        onRemove={handleDeleteProtocol}
      />
      <CopyProtocolDialog
        open={openCopyDialog}
        onClose={handleCancelCopy}
        protocolData={protocolToEdit}
        onSave={handleCopyProtocol}
      />
    </>
  );
};
